import { z } from 'zod/v4';

import {
    advancedBorderSettingsSchema,
    advancedRadiusSchema,
    buttonAdditionalEffectsSchema,
    buttonHorizontalPositionSchema,
    buttonIconSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    buttonWidthTypeSchema,
    shadowSettingsSchemaLegacy,
    themeButtonDataSchemaLegacy,
    themeButtonHoverSchemaLegacy,
} from '../../../common/schemas/legacy';

/**
 * Input/modal styling for the rendered form — mirrors `FormInputSettings`
 * from `libs/components/sp-form-input-settings`. Colours hold
 * `ThemeColorValue` (a CSS colour string or theme preset name) — typed as
 * `z.string()`. The `modal*` block is optional: it only exists once the user
 * has opened the modal-form settings.
 */
export const formInputSettingsSchema = z.object({
    showLabels: z.boolean().nullable(),
    inputBorderType: z.enum(['fullBorder', 'bottomBorder']).nullable(),
    formStyleType: z.enum(['vertical', 'horizontal']).nullable(),
    borderRadius: z.number().nullable(),
    inputSize: buttonSizeSchema.nullable(),
    horizontalPosition: buttonHorizontalPositionSchema.nullable(),
    backgroundColor: z.string().nullable(),
    borderColor: z.string().nullable(),
    textColor: z.string().nullable(),
    inputTextColor: z.string().nullable(),
    linkColor: z.string().nullish(),

    modalBackgroundColor: z.string().nullish(),
    modalBorderRadius: z.number().nullish(),
    modalPadding: z.number().nullish(),
    modalTitleTextColor: z.string().nullish(),
    modalShowLabels: z.boolean().nullish(),
    modalInputBorderType: z.enum(['fullBorder', 'bottomBorder']).nullish(),
    modalInputBackgroundColor: z.string().nullish(),
    modalInputBorderColor: z.string().nullish(),
    modalTextColor: z.string().nullish(),
    modalInputTextColor: z.string().nullish(),
    modalInputBorderRadius: z.number().nullish(),
    modalInputSize: buttonSizeSchema.nullish(),
});

/**
 * Submit-button styling — mirrors `FormButtonSettings` from
 * `libs/components/sp-form-button-settings` (a `ThemedBaseButton` plus
 * label/size/width and the advanced setting bags).
 */
export const formButtonSettingsSchema = z.object({
    // ThemedBaseButton side
    styleType: buttonStyleTypeSchema.nullable(),
    buttonBase: themeButtonDataSchemaLegacy,
    buttonActive: themeButtonDataSchemaLegacy.nullish(),
    buttonHover: themeButtonHoverSchemaLegacy.nullish(),

    label: z.string().nullable(),
    size: buttonSizeSchema.nullable(),
    widthType: buttonWidthTypeSchema.nullable(),
    horizontalFormWidthType: buttonWidthTypeSchema.nullish(),
    widthValue: z.number().nullable(),
    buttonIcon: buttonIconSchema.nullish(),
    additionalEffects: buttonAdditionalEffectsSchema.nullish(),
    isIndependentCorners: z.boolean().nullish(),
    advancedRadius: advancedRadiusSchema.nullish(),
    advancedBorderSettings: advancedBorderSettingsSchema.nullish(),
    isShadow: z.boolean().nullish(),
    shadowSettings: shadowSettingsSchemaLegacy.nullish(),
});

/**
 * Post-submit behaviour — mirrors `FormAdditionalSettings` from
 * `libs/components/sp-form-additional-settings`.
 */
export const formAdditionalSettingsSchema = z.object({
    actionAfterConfirmType: z.enum(['message', 'link']).nullable(),
    subscriptionMessage: z.string().nullish(),
    openLink: z.string().nullish(),
    updateExistingContact: z.boolean().nullish(),
    isEnabledCaptcha: z.boolean().nullish(),
    captchaLimit: z.number().nullish(),
    promoCodeSettings: z
        .object({
            isEnabled: z.boolean().nullable(),
            label: z.string().nullable(),
            placeholder: z.string().nullable(),
            selectedPromoCode: z.array(z.number()).nullable(),
        })
        .nullish(),
    isEnableCorporateEmail: z.boolean().nullish(),
});

export type FormInputSettingsData = z.infer<typeof formInputSettingsSchema>;
export type FormButtonSettingsData = z.infer<typeof formButtonSettingsSchema>;
export type FormAdditionalSettingsData = z.infer<typeof formAdditionalSettingsSchema>;
